import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { imgSrc } from "@/lib/img";
import { Seo } from "@/components/Seo";
import { AreaChallengeDialog } from "@/components/AreaChallengeDialog";
import { TierBadge } from "@/components/TierBadge";
import { Flame, Trophy, MapPin, ChevronDown, ChevronRight, Building2, MapPinned, HeartHandshake, Clock, PartyPopper, Users } from "lucide-react";

const RANK_STYLE = ["bg-amber-400 text-amber-950", "bg-neutral-300 text-neutral-700", "bg-amber-700/80 text-white"];

// Phase 12 — neighbourhood area row. Expanding fetches the detail payload:
// action breakdown, privacy-respecting top contributors, local collectives.
function AreaRow({ item, i, period, isMine, onChallenge }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const rankCls = RANK_STYLE[i] || "bg-forest/10 text-forest";

  useEffect(() => {
    if (!open || detail) return;
    api.areaDetail(item.area, period).then(setDetail).catch(() => setDetail(null));
  }, [open, detail, item.area, period]);

  const BREAKDOWN = detail && [
    { icon: Clock, label: "Hours logged", value: detail.breakdown.hours_logged },
    { icon: HeartHandshake, label: "Neighbours helped", value: detail.breakdown.help_requests },
    { icon: PartyPopper, label: "Events hosted", value: detail.breakdown.events_hosted },
  ];

  return (
    <div
      data-testid={`leaderboard-area-row-${item.area.replace(/\s+/g, "-")}`}
      className={`bg-white border rounded-xl overflow-hidden animate-fade-up ${isMine ? "border-terracotta ring-1 ring-terracotta/40" : "border-neutral-200"}`}
      style={{ animationDelay: `${i * 60}ms` }}
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-neutral-50 transition-colors"
      >
        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-display text-sm shrink-0 ${rankCls}`}>{i + 1}</div>
        <div className="w-11 h-11 rounded-full bg-forest/10 text-forest flex items-center justify-center shrink-0">
          <MapPinned className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-forest truncate">
            {item.area}
            {isMine && <Badge data-testid="area-mine-badge" className="ml-2 rounded-full bg-terracotta/10 text-terracotta border-none text-[10px] align-middle">Your area</Badge>}
          </div>
          <div className="text-xs text-neutral-500">{item.contributors} contributor{item.contributors === 1 ? "" : "s"} · {item.hours}h given</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-2xl text-forest leading-none">{item.actions}</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-1">community actions</div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-neutral-400" /> : <ChevronRight className="w-4 h-4 text-neutral-400" />}
      </button>
      {open && (
        <div className="border-t border-neutral-100 bg-neutral-50/60 px-4 py-4 space-y-4" data-testid={`area-detail-${item.area.replace(/\s+/g, "-")}`}>
          {!detail && <div className="text-sm text-neutral-500">Loading…</div>}
          {detail && (
            <>
              <div className="grid grid-cols-3 gap-2">
                {BREAKDOWN.map((b) => (
                  <div key={b.label} className="bg-white border border-neutral-200 rounded-lg p-3 text-center">
                    <b.icon className="w-4 h-4 text-terracotta mx-auto" />
                    <div className="font-display text-lg text-forest mt-1">{b.value}</div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-neutral-500 leading-tight">{b.label}</div>
                  </div>
                ))}
              </div>
              {detail.top_contributors.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Top contributors</div>
                  <div className="mt-2 space-y-2">
                    {detail.top_contributors.map((v, vi) => {
                      const line = (
                        <>
                          <span>#{vi + 1} · {v.name}<TierBadge hours={v.total_hours} className="ml-2" /></span>
                          <span className="font-medium text-forest">{v.actions} action{v.actions === 1 ? "" : "s"} · {v.hours}h</span>
                        </>
                      );
                      return v.user_id ? (
                        <Link key={`${v.name}-${vi}`} to={`/u/${v.user_id}`} className="flex items-center justify-between text-sm text-neutral-700 hover:text-forest transition-colors">{line}</Link>
                      ) : (
                        <div key={`${v.name}-${vi}`} className="flex items-center justify-between text-sm text-neutral-700">{line}</div>
                      );
                    })}
                  </div>
                </div>
              )}
              {detail.collectives.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Collectives in {item.area}</div>
                  <div className="mt-2 space-y-2">
                    {detail.collectives.map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-3 bg-white border border-neutral-200 rounded-lg px-3 py-2.5">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-forest truncate">{c.name}</div>
                          <div className="text-xs text-neutral-500">{c.members} member{c.members === 1 ? "" : "s"}</div>
                        </div>
                        <Link
                          to={`/collectives/${c.id}`}
                          data-testid={`area-collective-join-${c.id}`}
                          className="text-xs font-medium px-3.5 py-1.5 rounded-full bg-forest text-white hover:bg-forest-hover transition-colors shrink-0 inline-flex items-center gap-1"
                        >
                          <Users className="w-3 h-3" /> Join
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {onChallenge && (
                <button
                  type="button"
                  data-testid="area-detail-challenge-btn"
                  onClick={onChallenge}
                  className="inline-flex items-center gap-1.5 text-xs font-medium bg-terracotta text-white hover:bg-terracotta-hover rounded-full px-4 py-2 transition-colors"
                >
                  <Flame className="w-3.5 h-3.5" /> Challenge your neighbours
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CityRow({ item, i }) {
  const [open, setOpen] = useState(false);
  const rankCls = RANK_STYLE[i] || "bg-forest/10 text-forest";
  return (
    <div
      data-testid={`leaderboard-city-row-${item.city.replace(/\s+/g, "-")}`}
      className="bg-white border border-neutral-200 rounded-xl overflow-hidden animate-fade-up"
      style={{ animationDelay: `${i * 60}ms` }}
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-neutral-50 transition-colors"
      >
        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-display text-sm shrink-0 ${rankCls}`}>{i + 1}</div>
        <div className="w-11 h-11 rounded-full bg-forest/10 text-forest flex items-center justify-center shrink-0">
          <Building2 className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-forest truncate">{item.city}</div>
          <div className="text-xs text-neutral-500">{item.volunteer_count} volunteer{item.volunteer_count === 1 ? "" : "s"}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-2xl text-forest leading-none">{item.total_hours}h</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-1">approved</div>
        </div>
        {item.top_volunteers?.length > 0 && (open ? <ChevronDown className="w-4 h-4 text-neutral-400" /> : <ChevronRight className="w-4 h-4 text-neutral-400" />)}
      </button>
      {open && item.top_volunteers?.length > 0 && (
        <div className="border-t border-neutral-100 bg-neutral-50/60 px-4 py-3 space-y-2">
          <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Top volunteers</div>
          {item.top_volunteers.map((v, vi) => (
            <Link
              key={v.user_id}
              to={`/u/${v.user_id}`}
              className="flex items-center justify-between text-sm text-neutral-700 hover:text-forest transition-colors"
            >
              <span>#{vi + 1} · {v.name}</span>
              <span className="font-medium text-forest">{v.hours}h</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [searchParams] = useSearchParams();
  const initialTab = ["national", "local", "cities", "areas"].includes(searchParams.get("tab"))
    ? searchParams.get("tab") : "national"; // noticeboard challenge posts deep-link ?tab=areas
  const [scope, setScope] = useState(initialTab);
  const [loading, setLoading] = useState(true);

  // URL → state: deep-links like ?tab=areas (noticeboard challenge posts) must
  // switch the tab even when we're already mounted on /leaderboard.
  useEffect(() => {
    const t = searchParams.get("tab");
    if (["national", "local", "cities", "areas"].includes(t)) {
      setScope(prev => (prev === t ? prev : t));
    }
  }, [searchParams]);

  const [cityItems, setCityItems] = useState([]);
  const [cityPeriod, setCityPeriod] = useState("month");
  const [cityLoading, setCityLoading] = useState(false);

  const [areaItems, setAreaItems] = useState([]);
  const [areaPeriod, setAreaPeriod] = useState("month");
  const [areaLoading, setAreaLoading] = useState(false);

  const hasLocation = user?.lat != null && user?.lng != null;
  const myArea = user?.area || null;

  useEffect(() => {
    if (scope === "cities" || scope === "areas") return;
    setLoading(true);
    const p = scope === "local" && hasLocation
      ? api.leaderboard("local", user.lat, user.lng)
      : api.leaderboard("national");
    p.then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, [scope, hasLocation]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs

  useEffect(() => {
    if (scope !== "cities") return;
    setCityLoading(true);
    api.citiesLeaderboard(cityPeriod)
      .then((d) => setCityItems(d?.items || []))
      .catch(() => setCityItems([]))
      .finally(() => setCityLoading(false));
  }, [scope, cityPeriod]);

  useEffect(() => {
    if (scope !== "areas") return;
    setAreaLoading(true);
    api.areasLeaderboard(areaPeriod)
      .then((d) => setAreaItems(d?.items || []))
      .catch(() => setAreaItems([]))
      .finally(() => setAreaLoading(false));
  }, [scope, areaPeriod]);

  // Friendly rivalry nudge: where does the signed-in user's area stand?
  const myAreaIdx = myArea ? areaItems.findIndex((a) => a.area === myArea) : -1;
  const areaAbove = myAreaIdx > 0 ? areaItems[myAreaIdx - 1] : null;
  const areaGap = areaAbove ? areaAbove.actions - areaItems[myAreaIdx].actions : 0;

  // "Challenge your neighbours" share card — copy mirrors the backend's
  // challenge_message (the server recomputes from live aggregates on post).
  const [challengeOpen, setChallengeOpen] = useState(false);
  const challengeMessage =
    myAreaIdx === 0
      ? `${myArea} leads the UK with ${areaItems[0]?.actions} community actions this month — let's keep the lead!`
      : myAreaIdx > 0
        ? `${myArea} needs ${areaGap + 1} more action${areaGap + 1 === 1 ? "" : "s"} to overtake ${areaAbove?.area} this month — every approved hour, neighbour helped and event hosted counts!`
        : `${myArea} hasn't scored yet this month — one approved hour, one neighbour helped or one event hosted puts us on the map!`;

  const Row = ({ r, i }) => {
    const inner = (
      <>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-display text-sm shrink-0 ${RANK_STYLE[i] || "bg-forest/10 text-forest"}`}>
          {i + 1}
        </div>
        <Avatar className="w-11 h-11 shrink-0">
          <AvatarImage src={imgSrc(r.picture)} alt={r.name} />
          <AvatarFallback className="bg-forest text-white">{r.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-forest truncate">
            {r.name}
            <TierBadge hours={r.hours_total} className="ml-2" />
          </div>
          <div className="text-xs text-neutral-500 truncate">{r.city || "UK"}</div>
        </div>
        {r.streak_weeks > 0 && (
          <Badge className="rounded-full bg-terracotta/10 text-terracotta border-none shrink-0">
            <Flame className="w-3.5 h-3.5 mr-1" /> {r.streak_weeks} wk streak
          </Badge>
        )}
        <div className="text-right shrink-0">
          <div className="font-display text-2xl text-forest leading-none">{r.hours_30d}h</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-1">30 days · {r.hours_total}h total</div>
        </div>
      </>
    );
    const cls = "flex items-center gap-4 bg-white border border-neutral-200 rounded-xl p-4 animate-fade-up";
    return r.user_id ? (
      <Link to={`/u/${r.user_id}`} data-testid={`leaderboard-row-${i}`}
        className={`${cls} hover:-translate-y-0.5 hover:shadow-md transition-all`} style={{ animationDelay: `${i * 60}ms` }}>
        {inner}
      </Link>
    ) : (
      <div data-testid={`leaderboard-row-${i}`} className={cls} style={{ animationDelay: `${i * 60}ms` }}>{inner}</div>
    );
  };

  return (
    <div data-testid="leaderboard-page" className="max-w-3xl mx-auto px-6 py-10">
      <Seo title="Volunteer leaderboard" description="The most active volunteers of the last 30 days — approved hours, weekly streaks and all-time totals." />
      <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Community</div>
      <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">Volunteer leaderboard</h1>
      <p className="text-neutral-600 mt-2 text-sm max-w-md">Ranked by approved hours. First names and cities only — manage your visibility in profile settings.</p>

      <Tabs value={scope} onValueChange={setScope} className="mt-6">
        <TabsList className="rounded-full">
          <TabsTrigger data-testid="leaderboard-tab-national" value="national" className="rounded-full px-6">National</TabsTrigger>
          <TabsTrigger data-testid="leaderboard-tab-local" value="local" className="rounded-full px-6">
            <MapPin className="w-3.5 h-3.5 mr-1" /> Near me
          </TabsTrigger>
          <TabsTrigger data-testid="leaderboard-tab-cities" value="cities" className="rounded-full px-6">
            <Building2 className="w-3.5 h-3.5 mr-1" /> Cities
          </TabsTrigger>
          <TabsTrigger data-testid="leaderboard-tab-areas" value="areas" className="rounded-full px-6">
            <MapPinned className="w-3.5 h-3.5 mr-1" /> Areas
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {scope === "local" && !hasLocation && (
        <div data-testid="leaderboard-local-hint" className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-800">
          {user
            ? <>Pin your location in <Link to="/onboarding" className="underline font-medium">your profile</Link> to see volunteers within 30 miles of you.</>
            : <><Link to="/login" className="underline font-medium">Sign in</Link> and pin your location to see your local leaderboard.</>}
        </div>
      )}

      {scope === "cities" && (
        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-neutral-600">Cities ranked by approved volunteering hours.</div>
          <Select value={cityPeriod} onValueChange={setCityPeriod}>
            <SelectTrigger data-testid="leaderboard-period-select" className="w-[160px] rounded-full h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">This month</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {scope !== "cities" && scope !== "areas" && (
        <div className="mt-6 space-y-3">
          {loading && <div className="text-sm text-neutral-500">Loading…</div>}
          {!loading && rows.length === 0 && (scope === "national" || hasLocation) && (
            <div className="text-center py-16 bg-white border border-dashed border-neutral-300 rounded-2xl">
              <Trophy className="w-10 h-10 text-forest/30 mx-auto" />
              <div className="font-display text-xl text-forest mt-3">{scope === "local" ? "No local volunteers ranked yet" : "No approved hours yet"}</div>
              <p className="text-sm text-neutral-500 mt-1">{scope === "local" ? "Be the first — log hours and get them approved." : "Log hours and get them approved to appear here."}</p>
            </div>
          )}
          {!loading && rows.map((r, i) => <Row key={`${r.name}-${i}`} r={r} i={i} />)}
        </div>
      )}

      {scope === "areas" && (
        <>
          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-neutral-600">Neighbourhoods ranked by community actions — approved hours, neighbours helped and events hosted.</div>
            <Select value={areaPeriod} onValueChange={setAreaPeriod}>
              <SelectTrigger data-testid="leaderboard-area-period-select" className="w-[160px] rounded-full h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">This month</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {user && !myArea && (
            <div data-testid="leaderboard-area-hint" className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-800">
              Pin your location in <Link to="/onboarding" className="underline font-medium">your profile</Link> to put your area on the map — only contributions from members with a pinned location are counted.
            </div>
          )}

          {!areaLoading && myAreaIdx === 0 && (
            <div data-testid="leaderboard-area-nudge" className="mt-4 bg-forest text-white rounded-xl p-4 text-sm flex items-center gap-2.5 flex-wrap">
              <Trophy className="w-4 h-4 text-amber-300 shrink-0" />
              <span className="flex-1 min-w-[200px]"><span className="font-semibold">{myArea}</span> leads the UK {areaPeriod === "month" ? "this month" : "all-time"} — keep it going!</span>
              <button
                type="button"
                data-testid="area-challenge-open-btn"
                onClick={() => setChallengeOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs font-medium bg-white/10 hover:bg-white/20 border border-white/20 rounded-full px-4 py-2 transition-colors"
              >
                <Flame className="w-3.5 h-3.5 text-amber-300" /> Challenge your neighbours
              </button>
            </div>
          )}
          {!areaLoading && myAreaIdx > 0 && (
            <div data-testid="leaderboard-area-nudge" className="mt-4 bg-terracotta/10 border border-terracotta/30 rounded-xl p-4 text-sm text-forest flex items-center gap-2.5 flex-wrap">
              <Flame className="w-4 h-4 text-terracotta shrink-0" />
              <span className="flex-1 min-w-[200px]"><span className="font-semibold">{myArea}</span> is <span className="font-semibold">{areaGap} action{areaGap === 1 ? "" : "s"}</span> behind {areaAbove.area} — rally your neighbours!</span>
              <button
                type="button"
                data-testid="area-challenge-open-btn"
                onClick={() => setChallengeOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs font-medium bg-terracotta text-white hover:bg-terracotta-hover rounded-full px-4 py-2 transition-colors"
              >
                <Flame className="w-3.5 h-3.5" /> Challenge your neighbours
              </button>
            </div>
          )}

          <div className="mt-4 space-y-3">
            {areaLoading && <div className="text-sm text-neutral-500">Loading…</div>}
            {!areaLoading && areaItems.length === 0 && (
              <div className="text-center py-16 bg-white border border-dashed border-neutral-300 rounded-2xl">
                <MapPinned className="w-10 h-10 text-forest/30 mx-auto" />
                <div className="font-display text-xl text-forest mt-3">No area data yet</div>
                <p className="text-sm text-neutral-500 mt-1">Community actions roll up here once members pin a location.</p>
              </div>
            )}
            {!areaLoading && areaItems.map((a, i) => (
              <AreaRow key={a.area} item={a} i={i} period={areaPeriod} isMine={a.area === myArea}
                       onChallenge={a.area === myArea ? () => setChallengeOpen(true) : null} />
            ))}
          </div>

          {myArea && (
            <AreaChallengeDialog area={myArea} message={challengeMessage}
                                 open={challengeOpen} onOpenChange={setChallengeOpen} />
          )}
        </>
      )}

      {scope === "cities" && (
        <div className="mt-4 space-y-3">
          {cityLoading && <div className="text-sm text-neutral-500">Loading…</div>}
          {!cityLoading && cityItems.length === 0 && (
            <div className="text-center py-16 bg-white border border-dashed border-neutral-300 rounded-2xl">
              <Building2 className="w-10 h-10 text-forest/30 mx-auto" />
              <div className="font-display text-xl text-forest mt-3">No city data yet</div>
              <p className="text-sm text-neutral-500 mt-1">Approved hours will roll up here once volunteers set a location.</p>
            </div>
          )}
          {!cityLoading && cityItems.map((c, i) => <CityRow key={c.city} item={c} i={i} />)}
        </div>
      )}
    </div>
  );
}
